import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

/*
 * The one definition of an editable event.
 *
 * Two screens now edit the same row - the organizer's claim wizard and the
 * admin events board - and the thing that must not differ between them is what
 * a field is called, what counts as valid, and what gets sent. Those live here
 * so the second screen cannot quietly accept a 60-character title the first one
 * refuses, or send a shape the server maps differently.
 *
 * The markup stays with each screen: the wizard is a cream two-step wizard for
 * a stranger, the board is a dense sheet for whoever is on call. Reusing the
 * fields, the rules and the payload is what stops them drifting; reusing the
 * layout would only make both worse.
 */

/** The subset of an event these forms can edit. Both DTOs satisfy it. */
export interface EditableEvent {
  _id?: string;
  id?: string;
  title?: string;
  description?: string | null;
  websiteUrl?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  registrationStart?: string | null;
  registrationEnd?: string | null;
  type?: string | null;
  isPrivate?: boolean | null;
  lobbyMode?: string | null;
  brochureUrl?: string | null;
  venueName?: string | null;
  locationDescription?: string | null;
  participantsLimit?: number | null;
  logoUrl?: string | null;
}

/** What the server is sent. Absent is impossible here: every field is stated. */
export interface EventFormPayload {
  title: string;
  description: string | null;
  websiteUrl: string | null;
  startDate: string | null;
  endDate: string | null;
  type: string;
  isPrivate: boolean;
  lobbyMode: string;
  brochureUrl: string | null;
  venueName: string | null;
  locationDescription: string | null;
  participantsLimit: number | null;
  logoUrl: string | null;
  registrationStart: string | null;
  registrationEnd: string | null;
}

/* Control names are not reader-facing wording; these labels match the form. */
export const EVENT_FIELD_LABELS: Record<string, string> = {
  title: 'Event name',
  description: 'Description',
  websiteUrl: 'Website URL',
  startDate: 'Start date',
  endDate: 'End date',
  registrationStart: 'Registration opens',
  registrationEnd: 'Registration closes',
  type: 'Event type',
  isPrivate: 'Visibility',
  lobbyMode: 'Lobby mode',
  brochureUrl: 'Brochure',
  venueName: 'Venue',
  locationDescription: 'Address',
  participantsLimit: 'Attendee limit',
  logoUrl: 'Logo',
};

export function eventFieldLabel(name: string): string {
  return EVENT_FIELD_LABELS[name] ?? name;
}

/** `<input type="date">` wants YYYY-MM-DD and nothing else. */
export function formatDateForInput(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

/*
 * An Event runs at most this many days; anything longer is a community with
 * dates on it. The server refuses the same thing (Event.MAX_EVENT_DAYS), so
 * this is the friendly early copy of that rule, not the only one.
 */
export const MAX_EVENT_DAYS = 31;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Put one error on a control, or take it off, leaving its others alone. */
function toggleError(control: AbstractControl, key: string, on: boolean): void {
  const errors = { ...(control.errors ?? {}) };
  if (on) errors[key] = true;
  else delete errors[key];
  control.setErrors(Object.keys(errors).length ? errors : null);
}

/**
 * The date rules, reported on the end date because that is the control the
 * reader moves to fix them: an end before a start, and an Event that runs
 * longer than MAX_EVENT_DAYS.
 */
export function eventDateRangeValidator(): (
  group: AbstractControl
) => ValidationErrors | null {
  return (group: AbstractControl): ValidationErrors | null => {
    const startDate = group.get('startDate');
    const endDate = group.get('endDate');
    if (!startDate || !endDate) return null;

    const start = startDate.value ? new Date(startDate.value).getTime() : NaN;
    const end = endDate.value ? new Date(endDate.value).getTime() : NaN;
    const both = !Number.isNaN(start) && !Number.isNaN(end);

    const backwards = both && end < start;
    const tooLong =
      both && group.get('type')?.value !== 'Community' && (end - start) / DAY_MS > MAX_EVENT_DAYS;

    toggleError(endDate, 'endDateBeforeStartDate', backwards);
    toggleError(endDate, 'eventTooLong', tooLong);

    if (backwards) return { endDateBeforeStartDate: true };
    if (tooLong) return { eventTooLong: true };
    return null;
  };
}

/** The offer that goes with eventTooLong: keep the dates, change the type. */
export function switchToCommunity(form: FormGroup): void {
  form.get('type')?.setValue('Community');
  form.get('type')?.markAsDirty();
}

/**
 * Build the form both screens edit.
 *
 * No `tag` control on purpose: the short name is derived from the title
 * server-side and is not organizer-facing, and omitting it from a PATCH leaves
 * the stored one alone.
 */
export function buildEventForm(fb: FormBuilder, event: EditableEvent): FormGroup {
  const form = fb.group(
    {
      _id: [event._id ?? event.id ?? '', Validators.required],
      title: [event.title ?? '', [Validators.required, Validators.maxLength(40)]],
      description: [event.description || ''],
      // Optional, but when given it must be a real http(s) URL.
      websiteUrl: [event.websiteUrl || '', [Validators.pattern(/^(https?:\/\/)[^\s]+$/i)]],
      startDate: [formatDateForInput(event.startDate)],
      endDate: [formatDateForInput(event.endDate)],
      type: [event.type || 'Event'],
      isPrivate: [event.isPrivate ?? false],
      lobbyMode: [event.lobbyMode || 'GroupChat'],
      brochureUrl: [event.brochureUrl || ''],
      // The app renders WHERE as the venue's name over its address, two fields
      // since 9695d778 - one combined string printed the street as the headline.
      venueName: [event.venueName || ''],
      locationDescription: [event.locationDescription || ''],
      participantsLimit: [event.participantsLimit ?? null],
      logoUrl: [event.logoUrl || ''],
      registrationStart: [formatDateForInput(event.registrationStart)],
      registrationEnd: [formatDateForInput(event.registrationEnd)],
    },
    { validators: eventDateRangeValidator() }
  );

  // Re-judge the end date whenever the start or the type moves under it.
  for (const name of ['startDate', 'type']) {
    form.get(name)?.valueChanges.subscribe(() => {
      form.get('endDate')?.updateValueAndValidity();
    });
  }

  return form;
}

/** What a field's error should say, or '' while it is not worth saying yet. */
export function eventFieldError(
  form: FormGroup,
  fieldName: string,
  submitAttempted = false
): string {
  const field = form.get(fieldName);
  if (!field?.errors || !(field.touched || submitAttempted)) return '';

  const label = eventFieldLabel(fieldName);
  if (field.errors['required']) return `${label} is required`;
  if (field.errors['maxlength']) {
    const { requiredLength, actualLength } = field.errors['maxlength'];
    return `${label} must be ${requiredLength} characters or fewer (currently ${actualLength}) — please shorten it`;
  }
  if (field.errors['pattern']) {
    return fieldName === 'websiteUrl'
      ? 'Please enter a valid URL starting with http:// or https://'
      : `${label} format is invalid`;
  }
  if (field.errors['endDateBeforeStartDate']) return 'End date must be on or after the start date';
  if (field.errors['eventTooLong']) {
    return `An event can run for at most ${MAX_EVENT_DAYS} days. Anything longer is a community.`;
  }
  return '';
}

/** Fields currently blocking submission, named as the reader sees them. */
export function blockingEventFields(form: FormGroup): string[] {
  return Object.keys(form.controls)
    .filter((name) => name !== '_id' && form.get(name)?.invalid)
    .map(eventFieldLabel);
}

const orNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

const isoOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value ? new Date(value).toISOString() : null;

/** The form as the server wants it: every field stated, empties as null. */
export function eventFormPayload(form: FormGroup): EventFormPayload {
  const v = form.value;
  return {
    title: v.title,
    description: orNull(v.description),
    websiteUrl: orNull(v.websiteUrl),
    startDate: isoOrNull(v.startDate),
    endDate: isoOrNull(v.endDate),
    type: v.type,
    isPrivate: !!v.isPrivate,
    lobbyMode: v.lobbyMode,
    brochureUrl: orNull(v.brochureUrl),
    venueName: orNull(v.venueName),
    locationDescription: orNull(v.locationDescription),
    participantsLimit: v.participantsLimit || null,
    logoUrl: orNull(v.logoUrl),
    registrationStart: isoOrNull(v.registrationStart),
    registrationEnd: isoOrNull(v.registrationEnd),
  };
}

/** How a value reads in a change list. */
function describe(field: string, value: unknown): string {
  if (value === null || value === '' || value === undefined) return 'empty';
  if (field === 'isPrivate') return value ? 'Private' : 'Public';
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    return new Date(value).toLocaleDateString(undefined, { dateStyle: 'medium' });
  }
  const text = String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
}

/**
 * What this save would actually change, in words.
 *
 * The point of showing it is that the space belongs to somebody else: an admin
 * confirming a save should be agreeing to specific changes, not to the idea of
 * saving. Fields that did not move are not listed.
 */
export function describeEventChanges(
  before: EditableEvent,
  payload: EventFormPayload
): string[] {
  const previous: Record<string, unknown> = {
    ...before,
    startDate: isoOrNull(formatDateForInput(before.startDate)),
    endDate: isoOrNull(formatDateForInput(before.endDate)),
    registrationStart: isoOrNull(formatDateForInput(before.registrationStart)),
    registrationEnd: isoOrNull(formatDateForInput(before.registrationEnd)),
  };

  return Object.entries(payload)
    .filter(([field, next]) => {
      const was = previous[field] ?? null;
      const now = next ?? null;
      return field === 'isPrivate' ? !!was !== !!now : was !== now;
    })
    .map(
      ([field, next]) =>
        `${eventFieldLabel(field)}: ${describe(field, previous[field] ?? null)} → ${describe(
          field,
          next
        )}`
    );
}
